import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { rmaLatestOrders } from './latest-orders.style.js'
import { latestOrdersContext, fetchLatestOrdersContext, fetchProductsOfOrderContext } from 'components/ordering/rma/components/context/rma-context.js'
import { RmaOrderDTO } from 'components/ordering/rma/api/dtos/rma-order-dto.js'
import { RmaProductDTO } from 'components/ordering/rma/api/dtos/rma-product-dto.js'
import 'components/ordering/rma/components/product-row/product-row.js'


@customElement('csc-rma-latest-orders')
export class CSCRmaLatestOrders extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, rmaLatestOrders]

  @consume({ context: latestOrdersContext, subscribe: true })
  @state()
  private orders: RmaOrderDTO[] = []

  @consume({ context: fetchLatestOrdersContext, subscribe: true })
  private fetchLatestOrders?: () => Promise<void>

  @consume({ context: fetchProductsOfOrderContext, subscribe: true })
  private fetchProductsOfOrder?: (shipmentID: string, saleID: string) => Promise<RmaProductDTO[]>

  @state()
  private orderProducts = new Map<string, RmaProductDTO[]>()

  override connectedCallback() {
    super.connectedCallback()
    this.fetchLatestOrders?.()
  }

  private orderKey(order: RmaOrderDTO) {
    return `${order.shipmentId ?? ''}:${order.saleId}`
  }

  private handleToggle = async (order: RmaOrderDTO) => {
    const key = this.orderKey(order)
    if (this.orderProducts.has(key)) return
    const products = (await this.fetchProductsOfOrder?.(order.shipmentId ?? '', order.saleId)) ?? []
    this.orderProducts = new Map(this.orderProducts).set(key, products)
  }

  private renderColumn(label: ReturnType<typeof t>, value: string | null) {
    if (!value) return nothing
    return html`
      <cse-caption-description class="rma-latest-orders__column">
        <span slot="caption">${label}</span>
        ${value}
      </cse-caption-description>
    `
  }

  // The order date leads the row on its own, without a caption above it.
  private renderOrderDate(order: RmaOrderDTO) {
    if (!order.saleDate) return nothing
    return html`<cse-text class="rma-latest-orders__date" data-test="rma-latest-orders-date">${order.saleDate}</cse-text>`
  }

  private renderOrder(order: RmaOrderDTO) {
    const products = this.orderProducts.get(this.orderKey(order)) ?? []
    return html`
      <cse-accordion
        class="rma-latest-orders__order"
        data-test="rma-latest-orders-order"
        size="lg"
        @accordion-toggle=${() => this.handleToggle(order)}
      >
        <div slot="title" class="rma-latest-orders__header">
          ${this.renderOrderDate(order)}
          ${this.renderColumn(t('shipment_reference', { ns: 'csc-rma-latest-orders', default: 'Shipment reference' }), order.shipmentReference)}
          ${order.erpOrderNumber
            ? this.renderColumn(t('erp_number', { ns: 'csc-rma-latest-orders', default: 'ERP number' }), order.erpOrderNumber)
            : this.renderColumn(t('order_number', { ns: 'csc-rma-latest-orders', default: 'Order number' }), order.saleReference)}
          ${this.renderColumn(t('invoice_number', { ns: 'csc-rma-latest-orders', default: 'Invoice' }), order.invoiceNumber)}
          ${this.renderColumn(t('products', { ns: 'csc-rma-latest-orders', default: 'Products' }), String(order.countedResults))}
        </div>
        <cse-accordion-group slot="content" class="rma-latest-orders__content">
          ${products.map((product) => html`<csc-rma-product-row .productData=${product} hide-order-meta></csc-rma-product-row>`)}
        </cse-accordion-group>
      </cse-accordion>
    `
  }

  override render() {
    if (!this.orders.length) return html`<div class="rma-latest-orders" data-test="rma-latest-orders"></div>`
    return html`
      <div class="rma-latest-orders" data-test="rma-latest-orders">
        <cse-text as="h2" variant="heading-2" class="rma-latest-orders__title">
          ${t('latest_orders', { ns: 'csc-rma-latest-orders', default: 'Latest orders' })}
        </cse-text>
        <cse-accordion-group class="rma-latest-orders__group">
          ${this.orders.map((order) => this.renderOrder(order))}
        </cse-accordion-group>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-latest-orders': CSCRmaLatestOrders
  }
}
